import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';

import { useSettings } from './context/SettingsContext';
import { useAuth } from './context/AuthContext';
import { TokenProvider } from './context/TokenContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { ThemeProvider } from './context/ThemeContext';
import { LegifranceAuthProvider } from './macadamia-core/contexts/LegifranceAuthContext';

import LoadingSpinner from './components/shared/LoadingSpinner';
import Maintenance from './features/status/Maintenance';
import { MaintenanceGuard } from './hooks/useMaintenanceMode';

import Login from './features/auth/pages/Login';
import Register from './features/auth/pages/Register';
import ForgotPassword from './features/auth/pages/ForgotPassword';
import ResetPassword from './features/auth/pages/ResetPassword';
import VerifyEmailPage from './features/auth/pages/VerifyEmailPage';
import InterestsPage from './features/auth/pages/InterestsPage';
import Profile from './features/auth/pages/Profile';
import PublicProfile from './features/public-profile/pages/PublicProfile';
import NotificationsPage from './features/notifications/pages/NotificationsPage';
import MyFolders from './features/folders/pages/MyFolders';
import Jurisprudences from './features/jurisprudences/pages/Jurisprudences';
import JurisprudenceDetail from './features/jurisprudences/pages/JurisprudenceDetail';
import JurisprudenceSuiviPage from './features/jurisprudences/pages/JurisprudenceSuiviPage';
import AlertsCenterPage from './features/jurisprudences/pages/AlertsCenterPage';
import HALPage from './features/hal/pages/HALPage';
import Damia from './features/damia/pages/Damia';
import Documentation from './features/documentation/pages/Documentation';
import DossierEditorRoute from './macadamia-core/dossierEditor/DossierEditorRoute';
import Status from './features/status/Status';
import Callback from './features/status/LegifranceCallback';
import ProtectedRoute from './routing/ProtectedRoute';
import AuthenticatedRoute from './routing/AuthenticatedRoute';
import PublicRoute from './routing/PublicRoute';

// Lazy loading des pages qui utilisent Stripe ou des dépendances lourdes
const TokensPage = lazy(() => import('./features/tokens/pages/TokensPage'));
const BillingPage = lazy(() => import('./features/billing/pages/BillingPage'));

function App() {
  const { maintenanceMode, isLoading } = useSettings();
  const { user } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (maintenanceMode && user?.role !== 'admin') {
    return <Maintenance />;
  }

  return (
    <ThemeProvider>
      <TokenProvider>
        <WebSocketProvider>
          <MaintenanceGuard>
            <Routes>
              {/* Routes publiques et d'authentification */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <PublicRoute>
                    <Register />
                  </PublicRoute>
                }
              />
              <Route
                path="/mot-de-passe-oublie"
                element={
                  <PublicRoute>
                    <ForgotPassword />
                  </PublicRoute>
                }
              />
              <Route
                path="/reinitialiser-mot-de-passe"
                element={
                  <PublicRoute>
                    <ResetPassword />
                  </PublicRoute>
                }
              />
              <Route
                path="/verifier-email"
                element={
                  <AuthenticatedRoute>
                    <VerifyEmailPage />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path="/vos-centres-d-interet"
                element={
                  <AuthenticatedRoute>
                    <InterestsPage />
                  </AuthenticatedRoute>
                }
              />
              {/* Plan selection route disabled - users are automatically assigned free plan */}
              {/* <Route
              path="/choisir-plan"
              element={
                <AuthenticatedRoute>
                  <PlanSelectionPage />
                </AuthenticatedRoute>
              }
            /> */}

              {/* Route de profil - n'a pas besoin de l'écosystème Macadamia */}
              <Route
                path="/mon-profil"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />

              {/* Route de profil public */}
              <Route
                path="/profil/:id"
                element={
                  <ProtectedRoute>
                    <PublicProfile />
                  </ProtectedRoute>
                }
              />

              {/* Route de tokens - n'a pas besoin de l'écosystème Macadamia */}
              <Route
                path="/mes-tokens"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<LoadingSpinner />}>
                      <TokensPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              {/* Route de facturation - n'a pas besoin de l'écosystème Macadamia */}
              <Route
                path="/facturation"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<LoadingSpinner />}>
                      <BillingPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              {/* Route de notifications */}
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <NotificationsPage />
                  </ProtectedRoute>
                }
              />

              {/* Route de détails d'une jurisprudence */}
              <Route
                path="/jurisprudences/:id"
                element={
                  <ProtectedRoute>
                    <JurisprudenceDetail />
                  </ProtectedRoute>
                }
              />

              {/* Routes de jurisprudences */}
              <Route
                path="/jurisprudences"
                element={
                  <ProtectedRoute>
                    <Jurisprudences />
                  </ProtectedRoute>
                }
              />

              {/* Route des suivis de jurisprudences */}
              <Route
                path="/jurisprudences/suivi"
                element={
                  <ProtectedRoute>
                    <JurisprudenceSuiviPage />
                  </ProtectedRoute>
                }
              />

              {/* Centre d'alertes (P2) */}
              <Route
                path="/jurisprudences/alertes"
                element={
                  <ProtectedRoute>
                    <AlertsCenterPage />
                  </ProtectedRoute>
                }
              />

              {/* Route de HAL */}
              <Route
                path="/hal"
                element={
                  <ProtectedRoute>
                    <HALPage />
                  </ProtectedRoute>
                }
              />

              {/* Route de Damia - n'a pas besoin de l'écosystème Macadamia */}
              <Route
                path="/damia"
                element={
                  <ProtectedRoute>
                    <Damia />
                  </ProtectedRoute>
                }
              />

              {/* Route de Documentation */}
              <Route
                path="/documentation"
                element={
                  <ProtectedRoute>
                    <Documentation />
                  </ProtectedRoute>
                }
              />

              {/* Routes spécifiques à Macadamia */}
              <Route
                path="/dossiers"
                element={
                  <ProtectedRoute>
                    <LegifranceAuthProvider>
                      <MyFolders />
                    </LegifranceAuthProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dossiers/:id"
                element={
                  <ProtectedRoute>
                    <LegifranceAuthProvider>
                      <DossierEditorRoute />
                    </LegifranceAuthProvider>
                  </ProtectedRoute>
                }
              />

              {/* Autres routes */}
              <Route path="/status" element={<Status />} />
              <Route path="/callback" element={<Callback />} />
              <Route path="*" element={<Navigate to="/documentation" />} />
            </Routes>
          </MaintenanceGuard>
        </WebSocketProvider>
      </TokenProvider>
    </ThemeProvider>
  );
}

export default App;
